import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  TeamOutlined,
  GiftOutlined,
  DollarOutlined,
  FileTextOutlined,
  UsergroupAddOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import logo from "../../assets/images/wb-logo.jpeg";
import { primaryColor } from "../../utils/constants";

function AdminSidenav() {
  const { pathname } = useLocation();
  const [collapsed] = useState(false);

  const menuItems = [
    {
      key: "consultant-profile",
      path: "/consultant-profile",
      icon: <TeamOutlined />,
      label: "Consultants",
      description: "Manage consultant profiles",
    },
    // {
    //   key: "consultant",
    //   path: "/consultant",
    //   icon: <UserAddOutlined />,
    //   label: "Consultant Create",
    //   description: "Add new consultants",
    // },
    {
      key: "referalcode",
      path: "/referalcode",
      icon: <GiftOutlined />,
      label: "Referral Code",
      description: "Manage referral codes",
    },
    {
      key: "redeem-coin",
      path: "/redeem-coin",
      icon: <DollarOutlined />,
      label: "Redeem Coin",
      description: "Handle coin redemptions",
    },
    {
      key: "consultant-applications",
      path: "/consultant-applications",
      icon: <FileTextOutlined />,
      label: "Applications",
      description: "Review applications",
    },
    // {
    //   key: "become-consultant",
    //   path: "/become-consultant",
    //   icon: <UserSwitchOutlined />,
    //   label: "Become Consultant",
    //   description: "Consultant registration",
    // },
    {
      key: "users",
      path: "/users",
      icon: <UsergroupAddOutlined />,
      label: "Users",
      description: "Manage user accounts",
    },
    {
      key: "reports",
      path: "/reports",
      icon: <ExclamationCircleOutlined />,
      label: "Reports",
      description: "Manage reported content",
    },
  ];

  const isActive = (path) => pathname === path;

  const sidebarVariants = {
    expanded: {
      width: 260,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    collapsed: {
      width: 70,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      className="sidebar-container"
      variants={sidebarVariants}
      animate={collapsed ? "collapsed" : "expanded"}
      style={{
        height: "100vh",
        background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
        borderRight: `1px solid ${primaryColor}15`,
        boxShadow: "4px 0 20px rgba(164, 1, 101, 0.08)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header Section */}
      <div
        className="sidebar-header"
        // style={{ padding: collapsed ? "20px 10px" : "20px" }}
      >
        <div className="grid place-items-center">
          <img
            className="w-36 h-36 object-cover rounded-md"
            src={logo}
            alt="Logo"
          />
        </div>

        <hr
          style={{
            border: "none",
            height: "1px",
            background: `${primaryColor}20`,
            margin: "20px 0",
          }}
        />
      </div>

      {/* Navigation Menu */}
      <nav
        className="sidebar-nav"
        // style={{ padding: collapsed ? "0 8px" : "0 20px" }}
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.05,
              },
            },
          }}
        >
          {menuItems.map((item) => (
            <motion.div key={item.key} variants={itemVariants}>
              <NavLink
                to={item.path}
                className="nav-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: collapsed ? "12px 6px" : "12px 16px",
                  marginBottom: "8px",
                  borderRadius: "12px",
                  textDecoration: "none",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  background: isActive(item.path)
                    ? `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}CC 100%)`
                    : "transparent",
                  color: isActive(item.path) ? "white" : "#4a5568",
                  boxShadow: isActive(item.path)
                    ? `0 4px 12px ${primaryColor}30`
                    : "none",
                  transform: isActive(item.path)
                    ? "translateY(-1px)"
                    : "translateY(0)",
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    const navItem = e.currentTarget;
                    const label = navItem.querySelector(".nav-label");
                    const description =
                      navItem.querySelector(".nav-description");

                    navItem.style.background = `${primaryColor}08`;
                    navItem.style.transform = "translateY(-1px)";
                    navItem.style.boxShadow = `0 2px 8px ${primaryColor}15`;

                    if (label) label.style.color = primaryColor;
                    if (description)
                      description.style.color = `${primaryColor}CC`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    const navItem = e.currentTarget;
                    const label = navItem.querySelector(".nav-label");
                    const description =
                      navItem.querySelector(".nav-description");

                    navItem.style.background = "transparent";
                    navItem.style.transform = "translateY(0)";
                    navItem.style.boxShadow = "none";

                    if (label) label.style.color = "#2d3748";
                    if (description) description.style.color = "#718096";
                  }
                }}
              >
                {/* Active Indicator */}
                {isActive(item.path) && (
                  <motion.div
                    className="active-indicator"
                    layoutId="activeIndicator"
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "4px",
                      background: "white",
                      borderRadius: "0 4px 4px 0",
                    }}
                    initial={false}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}

                {/* Icon */}
                <motion.div
                  className="nav-icon"
                  style={{
                    fontSize: "18px",
                    minWidth: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  animate={{
                    scale: isActive(item.path) ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {React.cloneElement(item.icon, {
                    style: {
                      color: isActive(item.path) ? "white" : "inherit",
                    },
                  })}
                </motion.div>

                {/* Label and Description */}
                <AnimatePresence>
                  {!collapsed && (
                    <motion.div
                      className="nav-content"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      style={{ marginLeft: "12px", flex: 1 }}
                    >
                      <div
                        className="nav-label"
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          lineHeight: "1.2",
                          color: isActive(item.path) ? "white" : "#2d3748",
                        }}
                      >
                        {item.label}
                      </div>
                      <div
                        className="nav-description"
                        style={{
                          fontSize: "11px",
                          lineHeight: "1.2",
                          marginTop: "2px",
                          color: isActive(item.path)
                            ? "rgba(255,255,255,0.8)"
                            : "#718096",
                        }}
                      >
                        {item.description}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </NavLink>
            </motion.div>
          ))}
        </motion.div>
      </nav>

      {/* Custom Styles */}
      <style jsx="true">{`
        .nav-item:hover .nav-tooltip {
          opacity: 1 !important;
        }

        .sidebar-container {
          user-select: none;
        }

        .nav-item {
          position: relative;
        }

        .nav-item::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            transparent 0%,
            rgba(255, 255, 255, 0.1) 100%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
          border-radius: 12px;
        }

        .nav-item:hover::before {
          opacity: 1;
        }
      `}</style>
    </motion.div>
  );
}

export default AdminSidenav;
